import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useContext,
  useMemo
} from 'react'
import { toast as sonner } from 'sonner'

import { Toaster } from '@/components/ui/sonner'

type NotificationPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'

export type ConfigArgs = {
  top?: number
  bottom?: number
  duration?: number
  prefixCls?: string
  getContainer?: () => HTMLElement | ShadowRoot
  placement?: NotificationPlacement
  closeIcon?: React.ReactNode
  rtl?: boolean
  maxCount?: number
  props?: React.HTMLProps<HTMLDivElement>
}

export interface ArgsProps {
  message: React.ReactNode
  description?: React.ReactNode
  btn?: React.ReactNode
  key?: React.Key
  onClose?: () => void
  duration?: number | null
  icon?: React.ReactNode
  placement?: NotificationPlacement
  style?: React.CSSProperties
  className?: string
  readonly type?: 'success' | 'info' | 'error' | 'warning'
  onClick?: () => void
  closeIcon?: React.ReactNode
  props?: React.HTMLProps<HTMLDivElement>
  role?: 'alert' | 'status'
}

type StaticFn = (args: ArgsProps) => void

export type Toast = {
  success: StaticFn
  info: StaticFn
  warning: StaticFn
  error: StaticFn
  open: StaticFn
  destroy: (key?: React.Key) => void
  config: (config: ConfigArgs) => void
}

export const ToastContext = createContext<Toast | null>(null)

/**
 * Maps the legacy Ant-notification ArgsProps onto sonner's options. Ant uses
 * `duration` in SECONDS (null/0 = sticky); sonner uses milliseconds.
 */
function toId(key?: React.Key): string | number | undefined {
  return typeof key === 'bigint' ? key.toString() : key
}

function toSonnerOptions(args: ArgsProps) {
  return {
    id: toId(args.key),
    description: args.description,
    icon: args.icon,
    duration:
      args.duration === null || args.duration === 0
        ? Infinity
        : args.duration !== undefined
          ? args.duration * 1000
          : undefined,
    onDismiss: args.onClose,
    onAutoClose: args.onClose,
    // sonner accepts a ReactNode action; legacy `btn` was a ReactNode button.
    ...(args.btn ? { action: args.btn as unknown as undefined } : {})
  }
}

const toastApi: Toast = {
  success: (args) => sonner.success(args.message, toSonnerOptions(args)),
  info: (args) => sonner.info(args.message, toSonnerOptions(args)),
  warning: (args) => sonner.warning(args.message, toSonnerOptions(args)),
  error: (args) => sonner.error(args.message, toSonnerOptions(args)),
  open: (args) => sonner(args.message, toSonnerOptions(args)),
  destroy: (key) => sonner.dismiss(toId(key)),
  // Per-toast placement/positioning is configured on the <Toaster>; no-op here
  // to preserve the legacy API surface.
  config: () => {}
}

/**
 * `toast` prop kept optional for backwards compatibility with the previous
 * Ant-notification-based provider; it is no longer used.
 */
export function ToastProvider({
  children
}: PropsWithChildren<{ toast?: unknown }>): ReactElement {
  const value = useMemo(() => toastApi, [])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  )
}

export function useToast(): Toast {
  const toast = useContext(ToastContext)

  if (!toast) {
    throw new Error('useToast must be used inside <ToastProvider>')
  }

  return toast
}
